import type { SiteChatCsat, SiteChatMessage } from '../types';

/**
 * «Rate the conversation» (website chat CSAT, owner 2026-10-25): the pure rules the widget draws the card by. The server
 * decides for real (`SiteChatCsat.CanAnswer`, `ChatMessageCsatAdditionalData`); these mirror it so a card is drawn frozen
 * without asking, and the answer shows at once.
 */

/** How long a card takes answers (server: `SiteCsatSettings.AnswerWindow`). */
export const CSAT_ANSWER_DAYS = 7;
/** Longest comment, characters (server: `ChatMessageCsatAdditionalData.MaxCommentLength`). */
export const CSAT_COMMENT_MAX = 1000;
/** Scores up to this one ask «what could we do better?»; higher ones only thank. */
export const CSAT_LOW_MAX = 3;

const DAY_MS = 24 * 60 * 60 * 1000;

/** A whole score 1..5. */
export function isCsatScore(score: unknown): score is number {
    return typeof score === 'number' && Number.isInteger(score) && score >= 1 && score <= 5;
}

/**
 * Whether the card still takes the visitor's answer: nobody has written after it — no later message that is stored or
 * on its way (a greeting or trigger phrase, the AI reply being written and other notes do not count) — and it is not
 * older than a week. A refusal of the server (`csatStatus: 'closed'`) freezes it for good.
 */
export function csatAnswerable(messages: SiteChatMessage[], card: SiteChatMessage, now: number = Date.now()): boolean {
    if (card.notice !== 'csat' || card.csatStatus === 'closed')
        return false;
    const created = Date.parse(card.createdDate);
    if (!Number.isFinite(created) || now - created > CSAT_ANSWER_DAYS * DAY_MS)
        return false;
    return !messages.some(message => message.index > card.index && !message.local && !message.draft && !message.notice);
}

/** The comment as the server stores it: trimmed, null when blank, cut to {@link CSAT_COMMENT_MAX} (never inside a pair). */
export function normalizeCsatComment(comment: string): string | null {
    const text = comment.trim();
    if (!text)
        return null;
    if (text.length <= CSAT_COMMENT_MAX)
        return text;
    const code = text.charCodeAt(CSAT_COMMENT_MAX - 1);
    return text.slice(0, code >= 0xd800 && code <= 0xdbff ? CSAT_COMMENT_MAX - 1 : CSAT_COMMENT_MAX);
}

/**
 * The card with the visitor's new answer, as the server will store it — drawn before the server answers. `comment`
 * undefined or null keeps the stored one, '' removes it.
 */
export function withCsatAnswer(card: SiteChatMessage, score: number, comment?: string | null): SiteChatMessage {
    const stored = card.csat?.comment ?? null;
    const csat: SiteChatCsat = { score, comment: comment == null ? stored : normalizeCsatComment(comment) };
    return { ...card, csat };
}
